import { Link, useParams } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import { flavorDetails, type FlavorOriginBlock } from '@/mocks/flavorDetail';
import { nomoCharacters } from '@/mocks/characters';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const toneByIndex = [
  { wrap: 'border-primary-200/70', tag: 'text-primary-600', line: 'bg-primary-400' },
  { wrap: 'border-secondary-200/70', tag: 'text-secondary-700', line: 'bg-secondary-500' },
  { wrap: 'border-accent-200/70', tag: 'text-accent-700', line: 'bg-accent-500' },
];

export default function FlavorPage() {
  const { id } = useParams<{ id: string }>();
  useScrollReveal();

  const flavor = flavorDetails.find((item) => item.id === id);
  const character = flavor?.characterId
    ? nomoCharacters.find((item) => item.id === flavor.characterId)
    : undefined;

  if (!flavor) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <SiteNavbar />
        <main className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <p className="font-heading text-2xl text-foreground-950">这道味道还在熬制中</p>
            <p className="mt-3 text-sm text-foreground-600">它正在某处水土里，慢慢熬出属于自己的故事。</p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              返回首页
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteNavbar />
      <main>
        <section className="bg-background-100 pb-14 pt-24 md:pb-20 md:pt-32">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <nav className="reveal mb-8 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link
                to="/"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <a
                href="/#map"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                味道地图
              </a>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800">{flavor.dish}</span>
            </nav>

            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div className="reveal order-2 lg:order-1">
                <p className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                  {flavor.cityEn}
                </p>
                <h1 className="mt-4 font-heading text-4xl leading-tight text-foreground-950 md:text-6xl">
                  {flavor.dish}
                  <span className="ml-4 align-middle text-sm tracking-[0.24em] text-foreground-400 uppercase md:text-base">
                    {flavor.dishEn}
                  </span>
                </h1>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
                  {flavor.intro}
                </p>

                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-md bg-primary-50 px-4 py-3">
                    <p className="text-[11px] tracking-[0.2em] text-primary-600 uppercase">Place</p>
                    <p className="mt-1 text-sm text-foreground-800">{flavor.city}</p>
                  </div>
                  <div className="rounded-md bg-secondary-50 px-4 py-3">
                    <p className="text-[11px] tracking-[0.2em] text-secondary-700 uppercase">Flavor</p>
                    <p className="mt-1 text-sm text-foreground-800">{flavor.dish}</p>
                  </div>
                  <div className="rounded-md bg-accent-50 px-4 py-3">
                    <p className="text-[11px] tracking-[0.2em] text-accent-700 uppercase">Wish</p>
                    <p className="mt-1 text-sm text-foreground-800">{flavor.wish}</p>
                  </div>
                </div>
              </div>

              <div className="reveal order-1 lg:order-2" style={{ transitionDelay: '120ms' }}>
                <div className="relative mx-auto aspect-square w-full max-w-[440px] overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
                  <img
                    src={flavor.image}
                    alt={`${flavor.dish} NOMOISM 中国味道`}
                    title={`NOMOISM 一味 · ${flavor.dish} · ${flavor.city}`}
                    className="h-full w-full object-cover object-top"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3.5 py-1.5 text-[11px] tracking-[0.16em] text-background-50">
                    一味 · Flavor
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="reveal mb-12 md:mb-16">
              <div className="mb-6 flex items-center gap-3">
                <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                  Origin
                </span>
                <span className="h-px w-8 bg-primary-300"></span>
                <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                  一味 × 一境 × 一愿
                </span>
              </div>
              <h2 className="font-heading text-3xl tracking-[0.06em] text-foreground-950 md:text-4xl">
                这道味道，从哪里来
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {flavor.originBlocks.map((block: FlavorOriginBlock, index: number) => {
                const tone = toneByIndex[index] ?? toneByIndex[0];
                return (
                  <article
                    key={block.tag}
                    className={`reveal rounded-lg border ${tone.wrap} bg-background-50 p-6 md:p-7`}
                    style={{ transitionDelay: `${index * 120}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`h-px w-8 ${tone.line}`}></span>
                      <span className={`text-[11px] tracking-[0.24em] uppercase ${tone.tag}`}>
                        {block.en}
                      </span>
                    </div>
                    <h3 className="mt-5 font-heading text-2xl text-foreground-950">{block.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-foreground-600">{block.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="reveal mb-10">
              <div className="mb-6 flex items-center gap-3">
                <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                  Story
                </span>
                <span className="h-px w-8 bg-primary-300"></span>
                <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                  味道里的故事
                </span>
              </div>
              <h2 className="font-heading text-3xl tracking-[0.06em] text-foreground-950 md:text-4xl">
                一段味道
              </h2>
            </div>

            <div className="space-y-5">
              {flavor.story.map((paragraph, index) => (
                <p
                  key={paragraph}
                  className="reveal text-sm leading-relaxed text-foreground-600 md:text-base"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {flavor.quote && (
              <blockquote className="reveal mt-12 rounded-lg border border-accent-200/70 bg-accent-50 px-6 py-8 text-center md:px-10">
                <p className="font-heading text-lg leading-relaxed text-foreground-800 md:text-xl">
                  「{flavor.quote}」
                </p>
              </blockquote>
            )}
          </div>
        </section>

        {character && (
          <section className="bg-background-50 py-16 md:py-24">
            <div className="mx-auto max-w-6xl px-4 md:px-6">
              <div className="reveal mb-12">
                <div className="mb-6 flex items-center gap-3">
                  <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                    NOMO 灵
                  </span>
                  <span className="h-px w-8 bg-primary-300"></span>
                  <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                    它长成了谁
                  </span>
                </div>
                <h2 className="font-heading text-3xl tracking-[0.06em] text-foreground-950 md:text-4xl">
                  这道味道，长成了它
                </h2>
              </div>

              <Link
                to={`/character/${character.id}`}
                className="reveal group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors duration-300 hover:border-primary-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative h-[260px] w-full bg-background-100 md:h-full md:min-h-[300px]">
                    <img
                      src={character.image}
                      alt={`${character.name} NOMOISM 东方潮玩角色`}
                      title={`NOMOISM ${character.name} · ${character.flavor}`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-6 md:p-10">
                    <p className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                      {character.placeEn}
                    </p>
                    <h3 className="mt-3 font-heading text-3xl text-foreground-950">
                      {character.name}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                      {character.origin}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-primary-600">
                      进入 {character.name} 的世界
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}